import { BankOutlined, DownOutlined, FileZipOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import type { InvoiceStatus, PaymentStatus } from '@financiar/shared';
import { INVOICE_PDF_ARCHIVE_MAX, isNegativeAmount, subtractMoney } from '@financiar/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, DatePicker, Drawer, Dropdown, Empty, Input, Segmented, Select, Space, Table, Tag, Typography } from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { getApiError } from '../../api/client';
import type { InvoiceListItem } from '../../api/types';
import { ActiveSubscriptionBadge } from '../../components/ActiveSubscriptionBadge';
import { CardRow, MobileCardList } from '../../components/MobileCardList';
import { downloadBlob } from '../../lib/download';
import { DateText, MoneyText } from '../../lib/format';
import { useIsMobile } from '../../lib/responsive';
import { usePersistedState } from '../../lib/usePersistedState';
import { fetchClienti } from '../clienti/api';
import { useFirmaCurenta } from '../firme/FirmaCurentaContext';
import { fetchSeries } from '../serii/api';
import { BankImportModal } from './BankImportModal';
import {
  cancelFactura,
  deleteFactura,
  downloadFacturiPdfArchive,
  duplicateFactura,
  facturaPdfUrl,
  fetchFacturi,
  fetchFacturiSummary,
  sendEfactura,
  stornoFactura,
} from './api';
import { EfacturaTag } from './EfacturaTag';
import { FacturaView } from './FacturaViewPage';
import { FacturiSummaryCards } from './FacturiSummaryCards';
import { SagaImportModal } from './SagaImportModal';

const { Title, Text } = Typography;
const PAGE_SIZE = 20;

type FilterKey = 'ALL' | 'DRAFT' | 'ISSUED' | 'OVERDUE' | 'PAID' | 'STORNOED' | 'CANCELLED';
type SortBy = 'number' | 'issueDate' | 'client' | 'total' | 'remaining';

/** True when an issued invoice is past its due date and not fully paid — the
 * "Întârziată" badge and the "Restante" segment both key off this. */
function isOverdue(row: InvoiceListItem): boolean {
  return (
    row.status === 'ISSUED' &&
    row.paymentStatus !== 'PAID' &&
    row.dueDate !== null &&
    dayjs(row.dueDate).isBefore(dayjs(), 'day')
  );
}

/** Localized quick ranges for the date filter. dayjs() is "now", so these are
 * evaluated per render — cheap and always current. */
const DATE_PRESETS: { label: string; value: [Dayjs, Dayjs] }[] = [
  { label: 'Azi', value: [dayjs(), dayjs()] },
  { label: 'Ieri', value: [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')] },
  { label: 'Ultimele 7 zile', value: [dayjs().subtract(6, 'day'), dayjs()] },
  { label: 'Ultimele 30 de zile', value: [dayjs().subtract(29, 'day'), dayjs()] },
  { label: 'Luna aceasta', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
  { label: 'Luna trecută', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
  { label: 'Anul acesta', value: [dayjs().startOf('year'), dayjs().endOf('year')] },
  { label: 'Anul trecut', value: [dayjs().subtract(1, 'year').startOf('year'), dayjs().subtract(1, 'year').endOf('year')] },
];

/** The visible quick-period chips (matching the reference design). Selecting
 * one drives the same date range the manual RangePicker sets — the chip is
 * just a shortcut, so both feed one `range` state. 'CUSTOM' is not an option
 * the user can click: it's the derived state when the range came from the
 * picker and matches no preset (no chip lights up then). */
type PeriodKey = 'ALL' | 'THIS_MONTH' | 'LAST_MONTH' | 'THIS_QUARTER' | 'THIS_YEAR';

const PERIOD_OPTIONS: { label: string; value: PeriodKey }[] = [
  { label: 'Toate', value: 'ALL' },
  { label: 'Luna aceasta', value: 'THIS_MONTH' },
  { label: 'Luna trecută', value: 'LAST_MONTH' },
  { label: 'Trimestrul acesta', value: 'THIS_QUARTER' },
  { label: 'Anul acesta', value: 'THIS_YEAR' },
];

/** The `[from, to]` ISO tuple for a preset (null for "Toate"). Evaluated per
 * call so "acum" is always current. Quarter is computed without the dayjs
 * `quarterOfYear` plugin (not loaded): snap to day 1 first so the month
 * setter can never overflow (e.g. Jan 31 → Feb). */
function periodRange(key: PeriodKey): [string, string] | null {
  const now = dayjs();
  const iso = (from: Dayjs, to: Dayjs): [string, string] => [from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD')];
  switch (key) {
    case 'ALL':
      return null;
    case 'THIS_MONTH':
      return iso(now.startOf('month'), now.endOf('month'));
    case 'LAST_MONTH': {
      const m = now.subtract(1, 'month');
      return iso(m.startOf('month'), m.endOf('month'));
    }
    case 'THIS_QUARTER': {
      const base = now.startOf('month');
      const qStartMonth = Math.floor(now.month() / 3) * 3; // 0, 3, 6 or 9
      return iso(base.month(qStartMonth).startOf('month'), base.month(qStartMonth + 2).endOf('month'));
    }
    case 'THIS_YEAR':
      return iso(now.startOf('year'), now.endOf('year'));
  }
}

const SEGMENT_OPTIONS: { label: string; value: FilterKey }[] = [
  { label: 'Toate', value: 'ALL' },
  { label: 'Ciorne', value: 'DRAFT' },
  { label: 'Emise', value: 'ISSUED' },
  { label: 'Restante', value: 'OVERDUE' },
  { label: 'Încasate', value: 'PAID' },
  { label: 'Stornate', value: 'STORNOED' },
  { label: 'Anulate', value: 'CANCELLED' },
];

/**
 * Segment -> the filters `GET /invoices` actually supports.
 *
 * "Stornate" is the one segment the API has no filter for: whether an invoice
 * has been stornoed is DERIVED (`stornoedByIds`, the invoices that reference
 * it), not a column, so there is nothing to send. Per the task brief we send
 * the part the API does have (an invoice that has been stornoed is by
 * definition still ISSUED) and narrow the rest client-side, ON THE CURRENT
 * PAGE ONLY — the pagination footer therefore still counts every ISSUED
 * invoice, which is why the segment renders an explicit note saying so
 * instead of silently showing a misleading count.
 */
const API_FILTERS: Record<FilterKey, { status?: InvoiceStatus; paymentStatus?: PaymentStatus; unpaid?: boolean; overdue?: boolean }> = {
  ALL: {},
  DRAFT: { status: 'DRAFT' },
  // "Emise" = issued AND not fully paid (de încasat) — disjoint from
  // "Încasate", so a paid invoice shows only under "Încasate".
  ISSUED: { status: 'ISSUED', unpaid: true },
  OVERDUE: { status: 'ISSUED', overdue: true },
  PAID: { status: 'ISSUED', paymentStatus: 'PAID' },
  STORNOED: { status: 'ISSUED' },
  CANCELLED: { status: 'CANCELLED' },
};

function StatusTag({ row }: { row: InvoiceListItem }) {
  if (row.status === 'DRAFT') return <Tag>Ciornă</Tag>;
  if (row.status === 'CANCELLED') return <Tag color="default">Anulată</Tag>;
  if (row.paymentStatus === 'PAID') return <Tag color="green">Încasată</Tag>;
  if (row.paymentStatus === 'PARTIAL') return <Tag color="orange">Parțial încasată</Tag>;
  return <Tag color="blue">Emisă</Tag>;
}

export function FacturiListPage() {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { firmaId } = useFirmaCurenta();
  const isMobile = useIsMobile();

  // Filters persist across navigation/reload within the tab (usePersistedState).
  const [filter, setFilter] = usePersistedState<FilterKey>('facturi.filter', 'ALL');
  const [page, setPage] = usePersistedState('facturi.page', 1);
  const [search, setSearch] = usePersistedState('facturi.search', '');
  // Dayjs isn't JSON-serializable, so the range persists as an ISO tuple and
  // is rehydrated to dayjs for the picker.
  const [rangeIso, setRangeIso] = usePersistedState<[string, string] | null>('facturi.range', null);
  const range = useMemo<[Dayjs, Dayjs] | null>(
    () => (rangeIso ? [dayjs(rangeIso[0]), dayjs(rangeIso[1])] : null),
    [rangeIso],
  );
  // Which quick-period chip (if any) the current range corresponds to.
  // Derived from `rangeIso` rather than kept as separate state, so a persisted
  // range rehydrates the right chip on reload and a manual picker edit that
  // matches no preset simply lights none ('').
  const activePeriod = useMemo<PeriodKey | ''>(() => {
    if (!rangeIso) return 'ALL';
    for (const opt of PERIOD_OPTIONS) {
      if (opt.value === 'ALL') continue;
      const preset = periodRange(opt.value);
      if (preset && preset[0] === rangeIso[0] && preset[1] === rangeIso[1]) return opt.value;
    }
    return '';
  }, [rangeIso]);
  // Multi-select client filter. The selected clients persist as `labelInValue`
  // pairs so each tag keeps its NAME across reload/search even when that client
  // isn't in the current (server-paginated) options page — a bare-id filter
  // would render the tag as the raw UUID there.
  const [clients, setClients] = usePersistedState<{ value: string; label: string }[]>('facturi.clients', []);
  const clientIds = useMemo(() => clients.map((c) => c.value), [clients]);
  const [clientSearch, setClientSearch] = useState('');
  const [seriesId, setSeriesId] = usePersistedState<string | undefined>('facturi.seriesId', undefined);
  const [sagaImportOpen, setSagaImportOpen] = useState(false);
  const [bankImportOpen, setBankImportOpen] = useState(false);
  const [sort, setSort] = usePersistedState<{ by: SortBy; order: 'asc' | 'desc' } | undefined>('facturi.sort', undefined);
  // Split-view selection: transient (a reload starts on the full-width grid).
  // Clicking a row opens its detail in a panel on the right instead of routing
  // away, so you can page through invoices without losing the list.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // A selected invoice belongs to the firm it was opened under; switching firms
  // must not leave a foreign invoice pinned in the panel.
  useEffect(() => setSelectedId(null), [firmaId]);
  // Checked rows for bulk actions — kept across pages (the ids are what
  // matter), dropped on firm switch like the panel selection.
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  useEffect(() => setCheckedIds([]), [firmaId]);

  const apiFilters = API_FILTERS[filter];
  const dateFrom = range?.[0]?.format('YYYY-MM-DD');
  const dateTo = range?.[1]?.format('YYYY-MM-DD');

  const listParams = {
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
    clientIds: clientIds.length ? clientIds : undefined,
    seriesId,
    dateFrom,
    dateTo,
    sortBy: sort?.by,
    sortOrder: sort?.order,
    ...apiFilters,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['invoices', firmaId, listParams],
    queryFn: () => fetchFacturi({ companyId: firmaId as string, ...listParams }),
    enabled: !!firmaId,
  });

  const { data: clientsPage } = useQuery({
    queryKey: ['clients', firmaId, { page: 1, pageSize: 20, search: clientSearch }],
    queryFn: () => fetchClienti({ companyId: firmaId as string, page: 1, pageSize: 20, search: clientSearch || undefined }),
    enabled: !!firmaId,
  });

  const { data: series } = useQuery({
    queryKey: ['series', firmaId],
    queryFn: () => fetchSeries(firmaId as string),
    enabled: !!firmaId,
  });

  // The stat cards summarize the SCOPE only (period + client + series +
  // search) — deliberately NOT keyed on `filter` (status chips), `sort` or
  // `page`, so paging, sorting or flipping a status chip never refetches them.
  const { data: summary, isLoading: isLoadingSummary } = useQuery({
    queryKey: [
      'invoices',
      'summary',
      firmaId,
      { clientIds: clientIds.length ? clientIds : undefined, seriesId, search: search || undefined, dateFrom, dateTo },
    ],
    queryFn: () =>
      fetchFacturiSummary({
        companyId: firmaId as string,
        clientIds: clientIds.length ? clientIds : undefined,
        seriesId,
        search: search || undefined,
        dateFrom,
        dateTo,
      }),
    enabled: !!firmaId,
  });

  const seriesOptions = useMemo(
    () => (series ?? []).filter((s) => s.docType === 'INVOICE').map((s) => ({ value: s.id, label: s.prefix })),
    [series],
  );

  const rows = useMemo(() => {
    const items = data?.items ?? [];
    return filter === 'STORNOED' ? items.filter((row) => row.stornoedByIds.length > 0) : items;
  }, [data, filter]);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['invoices'] });
  }

  function reportError(error: unknown, fallback: string) {
    message.error(getApiError(error)?.message ?? fallback);
  }

  const deleteMutation = useMutation({
    mutationFn: deleteFactura,
    onSuccess: () => {
      message.success('Ciorna a fost ștearsă.');
      invalidate();
    },
    onError: (error) => reportError(error, 'Ciorna nu a putut fi ștearsă.'),
  });

  const cancelMutation = useMutation({
    mutationFn: cancelFactura,
    onSuccess: () => {
      message.success('Factura a fost anulată.');
      invalidate();
    },
    onError: (error) => reportError(error, 'Factura nu a putut fi anulată.'),
  });

  const stornoMutation = useMutation({
    mutationFn: stornoFactura,
    onSuccess: (storno) => {
      message.success(`Factura de stornare ${storno.fullNumber ?? ''} a fost emisă.`);
      invalidate();
      navigate(`/facturi/${storno.id}`);
    },
    onError: (error) => reportError(error, 'Factura nu a putut fi stornată.'),
  });

  const duplicateMutation = useMutation({
    mutationFn: duplicateFactura,
    onSuccess: (draft) => {
      message.success('Ciorna a fost creată prin duplicare.');
      invalidate();
      navigate(`/facturi/${draft.id}/editare`);
    },
    onError: (error) => reportError(error, 'Factura nu a putut fi duplicată.'),
  });

  const sendEfacturaMutation = useMutation({
    mutationFn: sendEfactura,
    onSuccess: () => {
      message.success('Factura a fost trimisă la e-Factura (ANAF).');
      invalidate();
    },
    onError: (error) => reportError(error, 'Trimiterea la e-Factura a eșuat.'),
  });

  function menuFor(row: InvoiceListItem): MenuProps['items'] {
    const isDraft = row.status === 'DRAFT';
    const isIssued = row.status === 'ISSUED';
    // The list projection carries no payment count, so `paymentStatus` is the
    // proxy for "has payments" that gates Anulează (the server requires zero
    // payments). The one case where the two disagree is a zero-total invoice,
    // which is derived PAID at issue with no payment behind it — it would be
    // cancellable but the menu hides the action. The view page, which has the
    // full `payments` array, gates on the real count.
    const hasPayments = row.paymentStatus !== 'UNPAID';
    return [
      { key: 'view', label: 'Vezi' },
      ...(isDraft ? [{ key: 'edit', label: 'Editează' }] : []),
      { key: 'pdf', label: 'Descarcă PDF' },
      ...(isIssued ? [{ key: 'payment', label: 'Încasare' }] : []),
      ...(isIssued ? [{ key: 'storno', label: 'Stornează' }] : []),
      // e-Factura: send an issued (non-proforma) invoice not already in flight.
      ...(isIssued && row.kind !== 'PROFORMA' && ['NOT_APPLICABLE', 'ERROR', 'REJECTED'].includes(row.efacturaStatus)
        ? [
            {
              key: 'efactura-send',
              label: row.efacturaStatus === 'NOT_APPLICABLE' ? 'Trimite la e-Factura' : 'Retrimite la e-Factura',
            },
          ]
        : []),
      // Ruling R26 server-side: a credit note can never be duplicated.
      ...(row.kind !== 'STORNO' ? [{ key: 'duplicate', label: 'Duplică' }] : []),
      ...(row.kind !== 'STORNO' ? [{ key: 'subscription', label: 'Creează abonament' }] : []),
      ...(isIssued && !hasPayments ? [{ type: 'divider' as const }, { key: 'cancel', label: 'Anulează', danger: true }] : []),
      ...(isDraft ? [{ type: 'divider' as const }, { key: 'delete', label: 'Șterge', danger: true }] : []),
    ];
  }

  function handleAction(row: InvoiceListItem, key: string) {
    switch (key) {
      case 'view':
        // Open in the side panel, same as clicking the row.
        setSelectedId(row.id);
        return;
      case 'edit':
        navigate(`/facturi/${row.id}/editare`);
        return;
      case 'pdf':
        window.open(facturaPdfUrl(row.id), '_blank', 'noopener,noreferrer');
        return;
      case 'payment':
        // The payment modal lives on the view page (next to the payment
        // list it changes); the flag just opens it on arrival.
        navigate(`/facturi/${row.id}?incasare=1`);
        return;
      case 'storno':
        modal.confirm({
          title: 'Stornezi factura?',
          content: `Se emite o factură de stornare pentru ${row.fullNumber ?? 'această factură'}, cu valori negative și număr propriu. Factura originală rămâne neschimbată.`,
          okText: 'Stornează',
          cancelText: 'Renunță',
          onOk: () => stornoMutation.mutateAsync(row.id),
        });
        return;
      case 'duplicate':
        duplicateMutation.mutate(row.id);
        return;
      case 'efactura-send':
        modal.confirm({
          title: row.efacturaStatus === 'NOT_APPLICABLE' ? 'Trimiți factura la e-Factura?' : 'Retrimiți factura la e-Factura?',
          content: `Se generează XML-ul UBL și se încarcă la ANAF pentru ${row.fullNumber ?? 'această factură'}. Operațiunea nu poate fi anulată.`,
          okText: 'Trimite',
          cancelText: 'Renunță',
          onOk: () => sendEfacturaMutation.mutateAsync(row.id),
        });
        return;
      case 'subscription':
        // Seed a new subscription from this invoice (client + lines) on the
        // Abonamente page.
        navigate(`/abonamente?dinFactura=${row.id}`);
        return;
      case 'cancel':
        modal.confirm({
          title: 'Anulezi factura?',
          content: 'Numărul rămâne alocat definitiv acestei facturi (seria nu poate avea goluri), dar factura nu mai reprezintă o creanță.',
          okText: 'Anulează factura',
          cancelText: 'Renunță',
          okButtonProps: { danger: true },
          onOk: () => cancelMutation.mutateAsync(row.id),
        });
        return;
      case 'delete':
        modal.confirm({
          title: 'Ștergi ciorna?',
          content: 'Ciorna și liniile ei se șterg definitiv.',
          okText: 'Șterge',
          cancelText: 'Renunță',
          okButtonProps: { danger: true },
          onOk: () => deleteMutation.mutateAsync(row.id),
        });
        return;
      default:
        return;
    }
  }

  async function handleDownloadArchive() {
    if (checkedIds.length > INVOICE_PDF_ARCHIVE_MAX) {
      message.warning(`Poți descărca cel mult ${INVOICE_PDF_ARCHIVE_MAX} facturi odată.`);
      return;
    }
    setArchiveLoading(true);
    try {
      const { blob, filename } = await downloadFacturiPdfArchive(checkedIds);
      downloadBlob(blob, filename);
    } catch (error) {
      reportError(error, 'Arhiva PDF nu a putut fi generată.');
    } finally {
      setArchiveLoading(false);
    }
  }

  // Server-side sort props for a column: `sorter: true` tells AntD not to sort
  // locally (the current page only) but to emit onChange so we re-query.
  const sortProps = (key: SortBy) => ({
    sorter: true as const,
    sortOrder: sort?.by === key ? ((sort.order === 'asc' ? 'ascend' : 'descend') as 'ascend' | 'descend') : null,
  });

  const columns: ColumnsType<InvoiceListItem> = [
    {
      title: 'Număr',
      key: 'number',
      width: 170,
      ...sortProps('number'),
      render: (_, row) => (
        <Space size={4}>
          {row.fullNumber ? <span className="num">{row.fullNumber}</span> : <Tag>Ciornă</Tag>}
          {row.kind === 'STORNO' ? <Tag color="red">Storno</Tag> : null}
        </Space>
      ),
    },
    { title: 'Dată', key: 'issueDate', width: 110, ...sortProps('issueDate'), render: (_, row) => <DateText value={row.issueDate} /> },
    {
      title: 'Client',
      key: 'client',
      dataIndex: 'clientName',
      ...sortProps('client'),
      render: (_, row) => (
        <>
          {row.clientName}
          <ActiveSubscriptionBadge active={row.clientHasActiveSubscription} />
        </>
      ),
    },
    {
      title: 'Total',
      key: 'total',
      width: 140,
      align: 'right',
      ...sortProps('total'),
      render: (_, row) => (
        <MoneyText value={row.totalAmount} className={isNegativeAmount(row.totalAmount) ? 'money-negative' : undefined} />
      ),
    },
    {
      title: 'Rest de încasat',
      key: 'remaining',
      width: 150,
      align: 'right',
      ...sortProps('remaining'),
      render: (_, row) => {
        if (row.status !== 'ISSUED') return <Text type="secondary">—</Text>;
        const rest = subtractMoney(row.totalAmount, row.paidAmount);
        return <MoneyText value={rest} className={isNegativeAmount(rest) ? 'money-negative' : undefined} />;
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 220,
      render: (_, row) => (
        <Space size={4} wrap>
          <StatusTag row={row} />
          {isOverdue(row) ? <Tag color="volcano">Întârziată</Tag> : null}
          {row.stornoedByIds.length > 0 ? <Tag color="red">Stornată</Tag> : null}
          <EfacturaTag status={row.efacturaStatus} />
        </Space>
      ),
    },
    {
      title: 'Acțiuni',
      key: 'actions',
      width: 110,
      render: (_, row) => (
        // The whole row selects the invoice; the menu must not trigger that
        // selection on its way to opening.
        <span onClick={(event) => event.stopPropagation()} role="presentation">
          <Dropdown menu={{ items: menuFor(row), onClick: ({ key }) => handleAction(row, key) }} trigger={['click']}>
            <Button size="small">
              Acțiuni <DownOutlined />
            </Button>
          </Dropdown>
        </span>
      ),
    },
  ];

  // With the detail panel open the list is squeezed to a narrow column, so it
  // drops to just what you scan by (number+date, client, total, status) — every
  // action stays available inside the panel itself.
  const compactColumns: ColumnsType<InvoiceListItem> = [
    {
      title: 'Număr',
      key: 'number',
      width: 130,
      render: (_, row) => (
        <div>
          <Space size={4}>
            {row.fullNumber ? <span className="num">{row.fullNumber}</span> : <Tag>Ciornă</Tag>}
            {row.kind === 'STORNO' ? <Tag color="red">Storno</Tag> : null}
          </Space>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <DateText value={row.issueDate} />
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Client',
      key: 'client',
      // Flex row so the name ellipsis-truncates in this narrow column while the
      // active-subscription badge stays pinned and visible (plain `ellipsis`
      // would clip the icon off long names).
      render: (_, row) => (
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.clientName}</span>
          <ActiveSubscriptionBadge active={row.clientHasActiveSubscription} />
        </div>
      ),
    },
    {
      title: 'Total',
      key: 'total',
      width: 120,
      align: 'right',
      render: (_, row) => (
        <MoneyText value={row.totalAmount} className={isNegativeAmount(row.totalAmount) ? 'money-negative' : undefined} />
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, row) => (
        <Space size={4} wrap>
          <StatusTag row={row} />
          {isOverdue(row) ? <Tag color="volcano">Întârziată</Tag> : null}
          {row.stornoedByIds.length > 0 ? <Tag color="red">Stornată</Tag> : null}
          <EfacturaTag status={row.efacturaStatus} compact />
        </Space>
      ),
    },
  ];

  const splitView = selectedId !== null;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 16,
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          Facturi
        </Title>
        {isMobile ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  { key: 'saga', icon: <UploadOutlined />, label: 'Importă din Saga' },
                  { key: 'bank', icon: <BankOutlined />, label: 'Importă extras bancar' },
                ],
                onClick: ({ key }) => (key === 'saga' ? setSagaImportOpen(true) : setBankImportOpen(true)),
              }}
            >
              <Button block style={{ flex: 1 }}>
                Importă <DownOutlined />
              </Button>
            </Dropdown>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              block
              style={{ flex: 1 }}
              onClick={() => navigate('/facturi/noua')}
              disabled={!firmaId}
            >
              Factură nouă
            </Button>
          </div>
        ) : (
          <Space>
            <Button icon={<UploadOutlined />} onClick={() => setSagaImportOpen(true)}>
              Importă din Saga
            </Button>
            <Button icon={<BankOutlined />} onClick={() => setBankImportOpen(true)}>
              Importă extras bancar
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/facturi/noua')} disabled={!firmaId}>
              Factură nouă
            </Button>
          </Space>
        )}
      </div>

      {!firmaId ? (
        <Empty description="Selectează sau adaugă mai întâi o firmă." />
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: 'wrap',
              alignItems: isMobile ? 'stretch' : 'center',
              gap: 12,
              marginBottom: 16,
            }}
          >
            {/* Chip-urile de status/perioadă: pe mobil scroll orizontal în
                containerul lor, ca să nu se strivească toate opțiunile. */}
            <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <Segmented<FilterKey>
                options={SEGMENT_OPTIONS}
                value={filter}
                onChange={(value) => {
                  setFilter(value);
                  setPage(1);
                }}
              />
            </div>
            <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <Segmented<PeriodKey | ''>
                options={PERIOD_OPTIONS}
                value={activePeriod}
                onChange={(value) => {
                  // Only real presets are clickable; '' is a derived-only state.
                  setRangeIso(periodRange(value as PeriodKey));
                  setPage(1);
                }}
              />
            </div>
            <DatePicker.RangePicker
              value={range}
              format="DD.MM.YYYY"
              placeholder={['De la', 'Până la']}
              presets={DATE_PRESETS}
              style={isMobile ? { width: '100%' } : undefined}
              onChange={(values) => {
                setRangeIso(
                  values && values[0] && values[1]
                    ? [values[0].format('YYYY-MM-DD'), values[1].format('YYYY-MM-DD')]
                    : null,
                );
                setPage(1);
              }}
            />
            <Select
              mode="multiple"
              labelInValue
              allowClear
              showSearch
              filterOption={false}
              maxTagCount="responsive"
              style={isMobile ? { width: '100%' } : { minWidth: 240, maxWidth: 460 }}
              placeholder="Toți clienții"
              value={clients}
              onSearch={setClientSearch}
              onChange={(values) => {
                setClients(
                  (values as { value: string | number; label?: unknown }[]).map((v) => ({
                    value: String(v.value),
                    label: String(v.label ?? ''),
                  })),
                );
                setPage(1);
              }}
              options={(clientsPage?.items ?? []).map((client) => ({ value: client.id, label: client.name }))}
            />
            <Select
              allowClear
              style={{ width: isMobile ? '100%' : 140 }}
              placeholder="Toate seriile"
              value={seriesId}
              onChange={(value: string | undefined) => {
                setSeriesId(value);
                setPage(1);
              }}
              options={seriesOptions}
            />
            <Input.Search
              placeholder="Caută după număr sau client"
              allowClear
              defaultValue={search}
              style={{ width: isMobile ? '100%' : 260 }}
              onSearch={(value) => {
                setSearch(value);
                setPage(1);
              }}
            />
          </div>

          <FacturiSummaryCards data={summary} loading={isLoadingSummary} />

          {filter === 'STORNOED' ? (
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Facturile stornate se filtrează pe pagina curentă — paginarea numără toate facturile emise.
            </Text>
          ) : null}

          {!isMobile && checkedIds.length > 0 ? (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 12,
                marginBottom: 12,
              }}
            >
              <Text strong>
                {checkedIds.length === 1 ? '1 factură selectată' : `${checkedIds.length} facturi selectate`}
              </Text>
              <Button type="primary" icon={<FileZipOutlined />} loading={archiveLoading} onClick={handleDownloadArchive}>
                Descarcă PDF-uri (ZIP)
              </Button>
              <Button onClick={() => setCheckedIds([])} disabled={archiveLoading}>
                Deselectează
              </Button>
            </div>
          ) : null}

          {isMobile ? (
            <>
              <MobileCardList<InvoiceListItem>
                dataSource={rows}
                rowKey={(row) => row.id}
                loading={isLoading}
                emptyText="Nicio factură."
                onCardClick={(row) => setSelectedId(row.id)}
                pagination={{ current: page, pageSize: PAGE_SIZE, total: data?.total ?? 0, onChange: setPage }}
                renderCard={(row) => (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                      <Space size={4} wrap style={{ minWidth: 0 }}>
                        {row.fullNumber ? <span className="num">{row.fullNumber}</span> : <Tag>Ciornă</Tag>}
                        {row.kind === 'STORNO' ? <Tag color="red">Storno</Tag> : null}
                      </Space>
                      <span onClick={(event) => event.stopPropagation()} role="presentation">
                        <Dropdown menu={{ items: menuFor(row), onClick: ({ key }) => handleAction(row, key) }} trigger={['click']}>
                          <Button size="small">
                            Acțiuni <DownOutlined />
                          </Button>
                        </Dropdown>
                      </span>
                    </div>
                    <div style={{ marginTop: 2 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        <DateText value={row.issueDate} />
                      </Text>
                    </div>
                    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', minWidth: 0 }}>
                      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                        {row.clientName}
                      </span>
                      <ActiveSubscriptionBadge active={row.clientHasActiveSubscription} />
                    </div>
                    <CardRow label="Total">
                      <MoneyText value={row.totalAmount} className={isNegativeAmount(row.totalAmount) ? 'money-negative' : undefined} />
                    </CardRow>
                    {row.status === 'ISSUED' ? (
                      <CardRow label="Rest de încasat">
                        {(() => {
                          const restAmount = subtractMoney(row.totalAmount, row.paidAmount);
                          return (
                            <MoneyText value={restAmount} className={isNegativeAmount(restAmount) ? 'money-negative' : undefined} />
                          );
                        })()}
                      </CardRow>
                    ) : null}
                    <div style={{ marginTop: 8 }}>
                      <Space size={4} wrap>
                        <StatusTag row={row} />
                        {isOverdue(row) ? <Tag color="volcano">Întârziată</Tag> : null}
                        {row.stornoedByIds.length > 0 ? <Tag color="red">Stornată</Tag> : null}
                        <EfacturaTag status={row.efacturaStatus} />
                      </Space>
                    </div>
                  </>
                )}
              />
              <Drawer
                open={splitView}
                placement="right"
                width="100%"
                closable={false}
                styles={{ body: { padding: 12 } }}
                onClose={() => setSelectedId(null)}
              >
                {selectedId ? (
                  <FacturaView id={selectedId} mode="panel" onClose={() => setSelectedId(null)} onOpenInvoice={setSelectedId} />
                ) : null}
              </Drawer>
            </>
          ) : (
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ flex: splitView ? '0 0 44%' : '1 1 100%', minWidth: 0 }}>
              <Table<InvoiceListItem>
                rowKey="id"
                columns={splitView ? compactColumns : columns}
                rowSelection={{
                  selectedRowKeys: checkedIds,
                  preserveSelectedRowKeys: true,
                  onChange: (keys) => setCheckedIds(keys as string[]),
                  // Ticking a box must not also open the row in the side panel.
                  renderCell: (_checked, _row, _index, node) => (
                    <span onClick={(event) => event.stopPropagation()} role="presentation">
                      {node}
                    </span>
                  ),
                }}
                dataSource={rows}
                loading={isLoading}
                rowClassName={(row) => (row.id === selectedId ? 'ant-table-row-selected' : '')}
                onChange={(_pagination, _filters, sorter) => {
                  // Table.onChange fires for pagination too — only act (and reset
                  // to page 1) when the SORT actually changed, otherwise clicking a
                  // page number would be immediately overwritten by setPage(1).
                  const s = Array.isArray(sorter) ? sorter[0] : sorter;
                  const next = s?.order && s.columnKey ? { by: s.columnKey as SortBy, order: (s.order === 'ascend' ? 'asc' : 'desc') as 'asc' | 'desc' } : undefined;
                  const changed = (next?.by ?? null) !== (sort?.by ?? null) || (next?.order ?? null) !== (sort?.order ?? null);
                  if (changed) {
                    setSort(next);
                    setPage(1);
                  }
                }}
                onRow={(row) => ({
                  style: { cursor: 'pointer' },
                  // Open the detail in the side panel instead of navigating away,
                  // so you can step through invoices without leaving the list.
                  onClick: () => setSelectedId(row.id),
                })}
                scroll={{ y: 'calc(100vh - 452px)' }}
                sticky
                pagination={{
                  current: page,
                  pageSize: PAGE_SIZE,
                  total: data?.total ?? 0,
                  onChange: setPage,
                  showSizeChanger: false,
                }}
                summary={() =>
                  // The total row's colSpans assume the full column set; it is a
                  // full-width feature, hidden while the list is in compact mode.
                  !splitView && data && rows.length > 0 ? (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={4}>
                          <Text strong>Total (filtrat, toate paginile)</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="right">
                          <Text strong>
                            <MoneyText value={data.totals.total} />
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="right">
                          <Text strong>
                            <MoneyText value={data.totals.remaining} />
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6} colSpan={2} />
                      </Table.Summary.Row>
                    </Table.Summary>
                  ) : null
                }
              />
            </div>
            {splitView && selectedId ? (
              <div style={{ flex: '1 1 56%', minWidth: 0 }}>
                <FacturaView id={selectedId} mode="panel" onClose={() => setSelectedId(null)} onOpenInvoice={setSelectedId} />
              </div>
            ) : null}
          </div>
          )}
        </>
      )}

      <SagaImportModal
        open={sagaImportOpen}
        onClose={() => setSagaImportOpen(false)}
        onDone={() => {
          // The imported invoices/clients land under whichever firm the XML's
          // FurnizorCIF resolved to — not necessarily the one currently
          // selected — so `firms` and `clients` are invalidated broadly and
          // only the current firm's own invoice list is targeted directly.
          queryClient.invalidateQueries({ queryKey: ['invoices', firmaId] });
          queryClient.invalidateQueries({ queryKey: ['firms'] });
          queryClient.invalidateQueries({ queryKey: ['clients'] });
        }}
      />

      <BankImportModal
        open={bankImportOpen}
        onClose={() => setBankImportOpen(false)}
        onDone={() => {
          // Same reasoning as the Saga import above: the statement's own
          // "Cod unic:" resolves the firm, and a new bank account may have
          // been created — invalidate broadly rather than assuming it's the
          // firm currently selected.
          queryClient.invalidateQueries({ queryKey: ['invoices', firmaId] });
          queryClient.invalidateQueries({ queryKey: ['firms'] });
        }}
      />
    </div>
  );
}
